import fs from 'fs-extra';
import path from 'path';
import { ImageProcessor } from '../utils/image';
import { SplashConfig } from '../types/config';
import { FlavorHelper } from '../core/flavor-helper';

export class WebPlatform {
  constructor(private flavorHelper: FlavorHelper) {}

  async generateSplash(config: SplashConfig): Promise<void> {
    console.log('[Web] Generating splash screen...');

    // Generate images
    await this.generateImages(config);

    // Generate background images or colors
    await this.generateBackgrounds(config);

    // Update index.html
    await this.updateIndexHtml(config);

    console.log('[Web] Splash screen generation complete!');
  }

  private async generateImages(config: SplashConfig): Promise<void> {
    const imagePath = config.imageWeb || config.image;
    const darkImagePath = config.darkImageWeb || config.darkImage;
    const brandingPath = config.brandingWeb || config.branding;
    const brandingDarkPath = config.brandingDarkWeb || config.brandingDark;

    // Create splash images folder
    await fs.ensureDir(this.flavorHelper.webSplashImagesFolder);

    // Main splash images
    if (imagePath) {
      await this.generateImageSet(imagePath, 'light');
    }

    if (darkImagePath) {
      await this.generateImageSet(darkImagePath, 'dark');
    }

    // Branding images
    if (brandingPath) {
      await this.generateImageSet(brandingPath, 'branding-light');
    }

    if (brandingDarkPath) {
      await this.generateImageSet(brandingDarkPath, 'branding-dark');
    }
  }

  private async generateImageSet(imagePath: string, prefix: string): Promise<void> {
    const templates = ImageProcessor.getWebTemplates(prefix);
    await ImageProcessor.processImage(
      imagePath,
      templates,
      this.flavorHelper.webSplashImagesFolder
    );
  }

  private async generateBackgrounds(config: SplashConfig): Promise<void> {
    const color = config.colorWeb || config.color;
    const darkColor = config.darkColorWeb || config.darkColor;
    const backgroundImage = config.backgroundImageWeb || config.backgroundImage;
    const darkBackgroundImage = config.darkBackgroundImageWeb || config.darkBackgroundImage;

    // Create splash background folder
    const backgroundFolder = path.join(this.flavorHelper.webSplashImagesFolder, 'background');
    await fs.ensureDir(backgroundFolder);

    // Light background
    if (backgroundImage) {
      await ImageProcessor.copyAndConvertImage(
        backgroundImage,
        path.join(backgroundFolder, 'light.png'),
        'png'
      );
    } else if (color) {
      await ImageProcessor.createSolidColorImage(
        color,
        1,
        1,
        path.join(backgroundFolder, 'light.png')
      );
    }

    // Dark background
    if (darkBackgroundImage) {
      await ImageProcessor.copyAndConvertImage(
        darkBackgroundImage,
        path.join(backgroundFolder, 'dark.png'),
        'png'
      );
    } else if (darkColor) {
      await ImageProcessor.createSolidColorImage(
        darkColor,
        1,
        1,
        path.join(backgroundFolder, 'dark.png')
      );
    }
  }

  private async updateIndexHtml(config: SplashConfig): Promise<void> {
    const indexPath = this.flavorHelper.webIndexFile;
    console.log(`[Web] Updating ${indexPath}`);

    if (!(await fs.pathExists(indexPath))) {
      console.warn('[Web] index.html not found, skipping update');
      return;
    }

    const content = await fs.readFile(indexPath, 'utf-8');
    const hasImage = !!(config.imageWeb || config.image);
    const hasDarkImage = !!(config.darkImageWeb || config.darkImage);
    const hasBranding = !!(config.brandingWeb || config.branding);
    const hasDarkBranding = !!(config.brandingDarkWeb || config.brandingDark);
    const imageMode = config.webImageMode || 'contain';
    const brandingMode = config.brandingMode || 'bottom';
    const brandingPadding = config.brandingBottomPadding || 0;

    // Generate splash screen styles
    const styles = this.generateSplashStyles(
      config,
      hasImage,
      hasDarkImage,
      hasBranding,
      hasDarkBranding,
      imageMode,
      brandingMode,
      brandingPadding
    );

    // Find where to insert splash styles
    let updatedContent: string;
    if (content.includes('</head>')) {
      updatedContent = content.replace('</head>', `${styles}\n  </head>`);
    } else {
      updatedContent = `${styles}\n${content}`;
    }

    await fs.writeFile(indexPath, updatedContent);
  }

  private generateSplashStyles(
    config: SplashConfig,
    hasImage: boolean,
    hasDarkImage: boolean,
    hasBranding: boolean,
    hasDarkBranding: boolean,
    imageMode: string,
    brandingMode: string,
    brandingPadding: number
  ): string {
    const color = config.colorWeb || config.color;
    const darkColor = config.darkColorWeb || config.darkColor;
    const fullscreen = config.fullscreen || false;

    const styles = `
  <style id="splash-screen-styles">
    #splash-screen {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: ${color ? `#${color}` : '#ffffff'};
      z-index: 9999;
      ${fullscreen ? 'height: 100vh;' : ''}
    }

    #splash-screen.hidden {
      opacity: 0;
      transition: opacity 0.5s ease-in-out;
      pointer-events: none;
    }

    ${hasImage ? this.generateImageStyles(imageMode) : ''}
    ${hasBranding ? this.generateBrandingStyles(brandingMode, brandingPadding) : ''}

    @media (prefers-color-scheme: dark) {
      #splash-screen {
        background-color: ${darkColor ? `#${darkColor}` : '#000000'};
      }

      ${hasDarkImage ? this.generateDarkImageStyles() : ''}
      ${hasDarkBranding ? this.generateDarkBrandingStyles() : ''}
    }
  </style>

  <div id="splash-screen">
    ${hasImage ? this.generateImageHtml() : ''}
    ${hasBranding ? this.generateBrandingHtml() : ''}
  </div>

  <script>
    window.addEventListener('load', function() {
      setTimeout(function() {
        var splash = document.getElementById('splash-screen');
        if (splash) {
          splash.classList.add('hidden');
          setTimeout(function() {
            splash.remove();
          }, 500);
        }
      }, 500);
    });
  </script>`;

    return styles;
  }

  private generateImageStyles(imageMode: string): string {
    return `
    #splash-screen-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: ${imageMode};
    }

    #splash-screen-image[src*="light"] {
      display: block;
    }

    #splash-screen-image[src*="dark"] {
      display: none;
    }
    `;
  }

  private generateDarkImageStyles(): string {
    return `
    #splash-screen-image[src*="light"] {
      display: none;
    }

    #splash-screen-image[src*="dark"] {
      display: block;
    }
    `;
  }

  private generateBrandingStyles(mode: string, padding: number): string {
    const position =
      mode === 'bottom'
        ? 'bottom: 0;'
        : mode === 'bottomLeft'
          ? 'bottom: 0; left: 0;'
          : mode === 'bottomRight'
            ? 'bottom: 0; right: 0;'
            : 'position: relative;';

    return `
    #splash-screen-branding {
      position: absolute;
      ${position}
      max-width: 50%;
      padding-bottom: ${padding}px;
    }

    #splash-screen-branding[src*="branding-light"] {
      display: block;
    }

    #splash-screen-branding[src*="branding-dark"] {
      display: none;
    }
    `;
  }

  private generateDarkBrandingStyles(): string {
    return `
    #splash-screen-branding[src*="branding-light"] {
      display: none;
    }

    #splash-screen-branding[src*="branding-dark"] {
      display: block;
    }
    `;
  }

  private generateImageHtml(): string {
    const sources = [1, 2, 3, 4]
      .map(density => `${this.flavorHelper.webSplashImagesFolder}light-${density}x.png ${density}x`)
      .join(', ');

    const darkSources = [1, 2, 3, 4]
      .map(density => `${this.flavorHelper.webSplashImagesFolder}dark-${density}x.png ${density}x`)
      .join(', ');

    return `
    <img
      id="splash-screen-image"
      src="${this.flavorHelper.webSplashImagesFolder}light-1x.png"
      srcset="${sources}"
      alt="Splash Screen"
    />
    <img
      id="splash-screen-image"
      src="${this.flavorHelper.webSplashImagesFolder}dark-1x.png"
      srcset="${darkSources}"
      alt="Splash Screen Dark"
      style="display: none;"
    />`;
  }

  private generateBrandingHtml(): string {
    const sources = [1, 2, 3, 4]
      .map(
        density =>
          `${this.flavorHelper.webSplashImagesFolder}branding-light-${density}x.png ${density}x`
      )
      .join(', ');

    const darkSources = [1, 2, 3, 4]
      .map(
        density =>
          `${this.flavorHelper.webSplashImagesFolder}branding-dark-${density}x.png ${density}x`
      )
      .join(', ');

    return `
    <img
      id="splash-screen-branding"
      src="${this.flavorHelper.webSplashImagesFolder}branding-light-1x.png"
      srcset="${sources}"
      alt="Branding"
    />
    <img
      id="splash-screen-branding"
      src="${this.flavorHelper.webSplashImagesFolder}branding-dark-1x.png"
      srcset="${darkSources}"
      alt="Branding Dark"
      style="display: none;"
    />`;
  }

  async removeSplash(): Promise<void> {
    console.log('[Web] Removing splash screen...');

    // Remove splash images folder
    const splashFolder = this.flavorHelper.webSplashFolder;
    if (await fs.pathExists(splashFolder)) {
      await fs.remove(splashFolder);
    }

    // Remove splash screen styles and div from index.html
    const indexPath = this.flavorHelper.webIndexFile;
    if (await fs.pathExists(indexPath)) {
      let content = await fs.readFile(indexPath, 'utf-8');

      // Remove styles
      content = content.replace(/<style id="splash-screen-styles">[\s\S]*?<\/style>/gm, '');

      // Remove splash screen div
      content = content.replace(/<div id="splash-screen">[\s\S]*?<\/div>/gm, '');

      // Remove splash screen script
      content = content.replace(
        /<script>[\s\S]*?window\.addEventListener\('load'[\s\S]*?<\/script>/gm,
        ''
      );

      await fs.writeFile(indexPath, content);
    }

    console.log('[Web] Splash screen removal complete!');
  }
}
